import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Col,
  Row,
  Tag,
  Typography,
  Space,
  Button,
  Input,
  Select,
  List,
  Avatar,
  Statistic,
  Modal,
  Form,
  Switch,
  Divider,
  Tooltip,
  Badge,
  Popconfirm,
  DatePicker,
  message,
  Spin,
  Empty,
} from "antd";
import {
  BellOutlined,
  PlusOutlined,
  SearchOutlined,
  CalendarOutlined,
  SyncOutlined,
  EyeOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  MinusCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import PageHeader from "../../components/headers/PageHeader";
import { METRO_COLORS } from "../../constants/colors";
import remindersService from "../../services/remindersService";
import { useUser } from "../../context/UserContext";
import dayjs from "dayjs";

const { Text, Title, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const CYCLE_LABELS = {
  yearly: "Anual",
  monthly: "Mensual",
  weekly: "Semanal",
  daily: "Diario",
  // "daily-nowkds" disabled — backend generates invalid cron expression (pending fix)
};

const CYCLE_COLORS = {
  yearly: "purple",
  monthly: "blue",
  weekly: "cyan",
  daily: "green",
};

// Normalize the API response — GET returns { Items, Count, ScannedCount }
const parseApiResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.Items)) return data.Items;
  if (Array.isArray(data?.reminders)) return data.reminders;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

// API does not return startDate/endDate — use date field to determine if upcoming
const isUpcoming = (item) => dayjs(item.date).isAfter(dayjs());

const fmt = (iso) => (iso ? dayjs(iso).format("DD/MM/YYYY HH:mm") : "—");

const DAYS_ES = ["domingos", "lunes", "martes", "miércoles", "jueves", "viernes", "sábados"];
const MONTHS_ES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const describeSchedule = (item) => {
  const d = dayjs(item.date);
  const time = d.format("HH:mm");
  const dayName = DAYS_ES[d.day()];
  const dayNum = d.date();
  const month = MONTHS_ES[d.month()];
  const year = d.year();

  if (!item.isCyclic) {
    return `Una sola vez — ${dayNum} de ${month} de ${year} a las ${time}`;
  }
  switch (item.cycle) {
    case "daily":   return `Todos los días a las ${time}`;
    case "weekly":  return `Cada semana los ${dayName} a las ${time}`;
    case "monthly": return `El día ${dayNum} de cada mes a las ${time}`;
    case "yearly":  return `Cada año el ${dayNum} de ${month} a las ${time}`;
    default:        return `Periódico (${item.cycle}) a las ${time}`;
  }
};

const formToRecord = (values) => ({
  name: values.name,
  description: values.description,
  date: values.date ? values.date.toISOString() : dayjs().toISOString(),
  startDate: values.range ? values.range[0].startOf("day").toISOString() : dayjs().toISOString(),
  endDate: values.range ? values.range[1].endOf("day").toISOString() : dayjs().add(1, "month").toISOString(),
  isCyclic: values.isCyclic || false,
  cycle: values.isCyclic ? values.cycle : null,
});

export default function RecordatoriosPage() {
  const { idToken } = useUser();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filterCycle, setFilterCycle] = useState("todos");

  const [createVisible, setCreateVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isCyclicCreate, setIsCyclicCreate] = useState(false);

  const [createForm] = Form.useForm();

  // --- Fetch ---
  const fetchReminders = useCallback(async () => {
    if (!idToken) return;
    setLoading(true);
    try {
      const data = await remindersService.obtenerReminders(idToken);
      setReminders(parseApiResponse(data));
    } catch (err) {
      console.error("Error al cargar recordatorios:", err);
      message.error("No se pudieron cargar los recordatorios");
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  // --- Create ---
  const openCreate = () => {
    createForm.resetFields();
    setIsCyclicCreate(false);
    setCreateVisible(true);
  };

  const handleCreate = async () => {
    const values = await createForm.validateFields();
    const record = formToRecord(values);
    setSaving(true);
    try {
      const created = await remindersService.crearReminder(record, idToken);
      // Service unwraps data.Item — use it directly if available, else refetch
      if (created?.id) {
        setReminders((prev) => [created, ...prev]);
      } else {
        await fetchReminders();
      }
      message.success("Recordatorio creado correctamente");
      setCreateVisible(false);
    } catch (err) {
      console.error("Error al crear recordatorio:", err);
      message.error("No se pudo crear el recordatorio");
    } finally {
      setSaving(false);
    }
  };

  // --- Delete ---
  const handleDelete = async (id) => {
    try {
      await remindersService.eliminarReminder(id, idToken);
      setReminders((prev) => prev.filter((r) => r.id !== id));
      message.success("Recordatorio eliminado");
    } catch (err) {
      console.error("Error al eliminar recordatorio:", err);
      message.error("No se pudo eliminar el recordatorio");
    }
  };

  // --- Detail (local data only) ---
  const openDetail = (item) => {
    setSelectedItem(item);
    setDetailVisible(true);
  };

  // --- Filters ---
  const filtered = reminders.filter((item) => {
    const matchesSearch =
      !searchText ||
      item.name.toLowerCase().includes(searchText.toLowerCase()) ||
      item.description.toLowerCase().includes(searchText.toLowerCase());
    const matchesCycle =
      filterCycle === "todos" ||
      (filterCycle === "no-ciclico" && !item.isCyclic) ||
      item.cycle === filterCycle;
    return matchesSearch && matchesCycle;
  });

  const stats = {
    total: reminders.length,
    proximos: reminders.filter(isUpcoming).length,
    ciclicos: reminders.filter((r) => r.isCyclic).length,
    unicaVez: reminders.filter((r) => !r.isCyclic).length,
  };

  return (
    <>
      <Card
        title={
          <PageHeader
            title="Recordatorios"
            head="Recordatorios"
            help={true}
            mensaje="Gestiona los recordatorios que se muestran a los operadores. Cada recordatorio tiene una fecha, un rango de vigencia y puede ser cíclico o de única vez. El banner superior siempre muestra el más reciente."
          />
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchReminders}
              loading={loading}
              disabled={!idToken}
            >
              Actualizar
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
              disabled={!idToken}
              style={{ background: METRO_COLORS.primary, borderColor: METRO_COLORS.primary }}
            >
              Nuevo recordatorio
            </Button>
          </Space>
        }
      >
        {/* Estadísticas */}
        <Row gutter={16} style={{ marginBottom: 20 }}>
          {[
            { title: "Total", value: stats.total, icon: <BellOutlined />, color: "#333" },
            { title: "Próximos", value: stats.proximos, icon: <CheckCircleOutlined />, color: METRO_COLORS.primary },
            { title: "Cíclicos", value: stats.ciclicos, icon: <SyncOutlined />, color: "#1890ff" },
            { title: "Única vez", value: stats.unicaVez, icon: <MinusCircleOutlined />, color: "#888" },
          ].map((s) => (
            <Col xs={12} sm={6} key={s.title}>
              <Card size="small" bordered style={{ borderRadius: 8, textAlign: "center" }}>
                <Statistic
                  title={s.title}
                  value={s.value}
                  prefix={s.icon}
                  valueStyle={{ color: s.color, fontSize: 24 }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Filtros */}
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={10}>
            <Search
              placeholder="Buscar por nombre o descripción..."
              allowClear
              prefix={<SearchOutlined />}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select value={filterCycle} onChange={setFilterCycle} style={{ width: "100%" }}>
              <Option value="todos">Todos los ciclos</Option>
              <Option value="no-ciclico">Única vez</Option>
              {Object.entries(CYCLE_LABELS).map(([key, label]) => (
                <Option key={key} value={key}>{label}</Option>
              ))}
            </Select>
          </Col>
        </Row>

        {/* Lista */}
        <Spin spinning={loading}>
          <List
            dataSource={filtered}
            locale={{
              emptyText: (
                <Empty
                  description="No hay recordatorios. Crea uno nuevo con el botón superior."
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
            renderItem={(item) => {
              const upcoming = isUpcoming(item);
              return (
                <List.Item
                  key={item.id}
                  style={{
                    background: upcoming ? "#fff" : "#fafafa",
                    borderRadius: 8,
                    marginBottom: 10,
                    padding: "12px 16px",
                    border: `1px solid ${upcoming ? "#c8e6c9" : "#f0f0f0"}`,
                    opacity: upcoming ? 1 : 0.75,
                    transition: "all 0.2s",
                  }}
                  actions={[
                    <Tooltip title="Ver detalle" key="view">
                      <Button type="text" icon={<EyeOutlined />} onClick={() => openDetail(item)} />
                    </Tooltip>,
                    <Popconfirm
                      key="delete"
                      title="¿Eliminar este recordatorio?"
                      description="Esta acción no se puede deshacer."
                      onConfirm={() => handleDelete(item.id)}
                      okText="Eliminar"
                      cancelText="Cancelar"
                      okButtonProps={{ danger: true }}
                    >
                      <Tooltip title="Eliminar">
                        <Button type="text" icon={<DeleteOutlined style={{ color: "#ff4d4f" }} />} />
                      </Tooltip>
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        style={{ background: upcoming ? METRO_COLORS.primary : "#ccc", fontSize: 16 }}
                        icon={<BellOutlined />}
                      />
                    }
                    title={
                      <Space size={8} wrap>
                        <Text strong style={{ fontSize: 14, color: upcoming ? "#222" : "#999" }}>
                          {item.name}
                        </Text>
                        {item.isCyclic ? (
                          <Tag color={CYCLE_COLORS[item.cycle] || "blue"} icon={<SyncOutlined />}>
                            {CYCLE_LABELS[item.cycle] || item.cycle}
                          </Tag>
                        ) : (
                          <Tag color="default">Única vez</Tag>
                        )}
                        <Badge
                          status={upcoming ? "success" : "default"}
                          text={
                            <Text style={{ fontSize: 11, color: "#888" }}>
                              {upcoming ? "Próximo" : "Pasado"}
                            </Text>
                          }
                        />
                      </Space>
                    }
                    description={
                      <Space size={4} style={{ marginTop: 4 }}>
                        <CalendarOutlined style={{ color: "#888", fontSize: 12 }} />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {describeSchedule(item)}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </Spin>
      </Card>

      {/* Modal — Detalle */}
      <Modal
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        title={
          <Space>
            <BellOutlined style={{ color: METRO_COLORS.primary }} />
            <span>Detalle del recordatorio</span>
          </Space>
        }
        width={560}
      >
        {selectedItem && (
          <>
            <Space style={{ marginBottom: 12 }} wrap>
              {selectedItem.isCyclic ? (
                <Tag color={CYCLE_COLORS[selectedItem.cycle] || "blue"} icon={<SyncOutlined />}>
                  {CYCLE_LABELS[selectedItem.cycle] || selectedItem.cycle}
                </Tag>
              ) : (
                <Tag color="default">Única vez</Tag>
              )}
              <Badge
                status={isUpcoming(selectedItem) ? "success" : "default"}
                text={isUpcoming(selectedItem) ? "Próximo" : "Pasado"}
              />
            </Space>
            <Title level={5} style={{ marginTop: 0 }}>{selectedItem.name}</Title>
            <Paragraph style={{ color: "#555", lineHeight: 1.7 }}>{selectedItem.description}</Paragraph>
            <Divider style={{ margin: "12px 0" }} />
            <Row gutter={16}>
              <Col span={24} style={{ marginBottom: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Frecuencia</Text>
                <br />
                <Text strong>{describeSchedule(selectedItem)}</Text>
              </Col>
              <Col span={12} style={{ marginBottom: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Fecha de referencia</Text>
                <br />
                <Text strong>{fmt(selectedItem.date)}</Text>
              </Col>
              {selectedItem.createdAt && (
                <Col span={12} style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Creado</Text>
                  <br />
                  <Text strong>{fmt(selectedItem.createdAt)}</Text>
                </Col>
              )}
            </Row>
          </>
        )}
      </Modal>

      {/* Modal — Crear */}
      <Modal
        open={createVisible}
        onCancel={() => setCreateVisible(false)}
        onOk={handleCreate}
        confirmLoading={saving}
        okText="Crear"
        cancelText="Cancelar"
        okButtonProps={{
          style: { background: METRO_COLORS.primary, borderColor: METRO_COLORS.primary },
        }}
        title={
          <Space>
            <PlusOutlined style={{ color: METRO_COLORS.primary }} />
            <span>Nuevo recordatorio</span>
          </Space>
        }
        width={560}
      >
        <Form form={createForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Nombre"
            rules={[{ required: true, message: "El nombre es obligatorio" }]}
          >
            <Input placeholder="Nombre del recordatorio" maxLength={120} showCount />
          </Form.Item>

          <Form.Item
            name="description"
            label="Descripción"
            rules={[{ required: true, message: "La descripción es obligatoria" }]}
          >
            <TextArea rows={3} placeholder="Descripción detallada..." maxLength={500} showCount />
          </Form.Item>

          <Form.Item
            name="date"
            label="Fecha del recordatorio"
            rules={[{ required: true, message: "La fecha es obligatoria" }]}
          >
            <DatePicker
              showTime
              format="DD/MM/YYYY HH:mm"
              style={{ width: "100%" }}
              placeholder="Seleccionar fecha y hora"
            />
          </Form.Item>

          <Form.Item
            name="range"
            label="Rango de vigencia (inicio — fin)"
            extra="El inicio debe ser una fecha futura"
            rules={[{ required: true, message: "El rango de vigencia es obligatorio" }]}
          >
            <RangePicker
              format="DD/MM/YYYY"
              style={{ width: "100%" }}
              placeholder={["Fecha inicio", "Fecha fin"]}
              disabledDate={(current) => current && current.isBefore(dayjs(), "day")}
            />
          </Form.Item>

          <Row gutter={12} align="middle">
            <Col span={12}>
              <Form.Item name="isCyclic" label="¿Es cíclico?" valuePropName="checked">
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                  onChange={setIsCyclicCreate}
                />
              </Form.Item>
            </Col>
            {isCyclicCreate && (
              <Col span={12}>
                <Form.Item
                  name="cycle"
                  label="Ciclo"
                  rules={[{ required: true, message: "Selecciona el ciclo" }]}
                >
                  <Select placeholder="Seleccionar...">
                    {Object.entries(CYCLE_LABELS).map(([key, label]) => (
                      <Option key={key} value={key}>{label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      </Modal>
    </>
  );
}
